import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { isDemoBrand, demoHash } from '../../../utils/demoBrand';
import {
  Loader2,
  RefreshCw,
  Play,
  Image as ImageIcon,
  ExternalLink,
  Bookmark,
  MousePointerClick,
  Eye,
  BarChart,
  BarChart3,
  ShoppingCart,
  TrendingUp,
  Layers,
  Settings,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// Static Pinterest creative rows for demo brands (aggregated by pin_id in the page).
// picsum stands in for pin images; video fields left empty so cards show the image.
function buildDemoPinterestPins() {
  const titles = [
    'Yeni Sezon Elbise Koleksiyonu', 'Sneaker Trendleri 2026', 'El Yapımı Deri Çanta',
    'Minimal Aksesuar Seti', 'Oversize Tişört Kombinleri', 'Sonbahar Ceket Rehberi',
    'Günlük Pantolon Stilleri', 'Konforlu İç Giyim', 'Kışlık Kaban İndirimi', 'Spor Ayakkabı Kampanyası',
  ];
  const campaigns = ['Consideration - Fashion', 'Conversions - Retargeting', 'Video Views - Brand'];
  return titles.map((t, i) => {
    const h = demoHash('pinpin' + t);
    const impressions = 30000 + (h % 260000);
    const clicks = Math.round(impressions * (0.006 + (h % 18) / 1000));
    const spend = Math.round(impressions * (0.7 + (h % 50) / 100) / 100);
    const conversions = Math.round(clicks * (0.02 + (h % 22) / 1000));
    const img = `https://picsum.photos/seed/pincr${i}/600/900`;
    return {
      pin_id: `demopin${100000 + i}`,
      ad_id: `demo-ad-${i}`,
      ad_name: `${t} — Pin`,
      campaign_name: campaigns[h % campaigns.length],
      ad_group_name: `Ad Group ${1 + (h % 4)}`,
      pin_title: t,
      pin_description: 'Sezonun trendlerini keşfet, Glowytics Demo Store’da.',
      pin_link: 'https://demo.glowytics.com',
      pin_url: 'https://www.pinterest.com/',
      creative_type: 'REGULAR',
      is_video: false,
      image_url_400: img, image_url_600: img, image_url_1200: img, image_url_original: img,
      video_url: null, video_thumbnail_url: null,
      dominant_color: '#E60023',
      spend, impressions, clicks, conversions,
      conversions_value: Math.round(conversions * (450 + (h % 600))),
      total_checkout: Math.round(conversions * (0.7 + (h % 30) / 100)),
      total_web_sessions: Math.round(clicks * (0.6 + (h % 40) / 100)),
      outbound_clicks: Math.round(clicks * (0.8 + (h % 20) / 100)),
      engagement: Math.round(impressions * (0.01 + (h % 15) / 1000)),
      video_3sec_views: 0, video_mrc_views: 0, video_avg_watchtime: 0,
      save_rate: (h % 300) / 10000,
      engagement_rate: (h % 500) / 10000,
    };
  });
}

const DATE_RANGE_OPTIONS = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last14', label: 'Last 14 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

function fmtTRY(n) {
  return '₺' + Math.round(Number(n || 0)).toLocaleString('tr-TR');
}
function fmtNum(n) {
  return Number(n || 0).toLocaleString('tr-TR');
}
function fmtPct(n) {
  return ((Number(n || 0)) * 100).toFixed(2) + '%';
}
function fmtLarge(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(2).replace(/\.?0+$/, '') + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return Math.round(v).toLocaleString('tr-TR');
}
function safe(n) { return Number(n || 0); }

export default function PinterestCreativePerformance() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('last30');
  const [creativeTypeFilter, setCreativeTypeFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });
  const [selectedPinId, setSelectedPinId] = useState(null);
  const [showSyncMenu, setShowSyncMenu] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        setError('No brand selected');
        setRows([]);
        return;
      }
      const resp = await axios.get(`${API_BASE}/api/pinterest/ad-performance-with-pins`, {
        params: { dateRange },
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId },
      });
      const apiRows = Array.isArray(resp?.data?.rows) ? resp.data.rows : [];
      setRows((apiRows.length === 0 && isDemoBrand(brandId)) ? buildDemoPinterestPins() : apiRows);
    } catch (e) {
      if (isDemoBrand(localStorage.getItem('currentBrandId'))) {
        setRows(buildDemoPinterestPins());
        setError('');
      } else {
        setError(e?.response?.data?.error || e.message || 'Failed to load');
        setRows([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const triggerSync = async (which) => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    setSyncing(true);
    setSyncStatus(`Running ${which}…`);
    try {
      const path = which === 'ad-performance' ? '/api/pinterest/sync/ad-performance' : '/api/pinterest/sync/pin-details';
      const resp = await axios.post(`${API_BASE}${path}`, {}, {
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId },
      });
      setSyncStatus(JSON.stringify(resp.data));
      setShowSyncMenu(false);
      await load();
    } catch (e) {
      setSyncStatus(`Error: ${e?.response?.data?.error || e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  // Aggregate by pin_id (creative)
  const pinAgg = useMemo(() => {
    const agg = {};
    for (const r of rows) {
      const key = r.pin_id || `__noPin_${r.ad_id}`;
      if (!agg[key]) {
        agg[key] = {
          pin_id: r.pin_id || null,
          ad_ids: new Set(),
          ad_names: new Set(),
          campaign_names: new Set(),
          ad_group_names: new Set(),
          pin_title: r.pin_title || null,
          pin_description: r.pin_description || null,
          pin_link: r.pin_link || null,
          pin_url: r.pin_url || (r.pin_id ? `https://www.pinterest.com/pin/${r.pin_id}/` : null),
          creative_type: r.creative_type || null,
          is_video: !!r.is_video,
          image_url_400: r.image_url_400 || null,
          image_url_600: r.image_url_600 || null,
          image_url_1200: r.image_url_1200 || null,
          image_url_original: r.image_url_original || null,
          video_url: r.video_url || null,
          video_thumbnail_url: r.video_thumbnail_url || null,
          dominant_color: r.dominant_color || null,
          spend: 0, impressions: 0, clicks: 0, conversions: 0, conversions_value: 0,
          total_checkout: 0, total_web_sessions: 0,
          outbound_clicks: 0, engagement: 0,
          video_3sec_views: 0, video_mrc_views: 0, video_watchtime_sum: 0, video_rows: 0,
          save_rate_sum: 0, engagement_rate_sum: 0, ctr_rows: 0,
        };
      }
      const a = agg[key];
      a.ad_ids.add(r.ad_id);
      if (r.ad_name) a.ad_names.add(r.ad_name);
      if (r.campaign_name) a.campaign_names.add(r.campaign_name);
      if (r.ad_group_name) a.ad_group_names.add(r.ad_group_name);
      a.spend += safe(r.spend);
      a.impressions += safe(r.impressions);
      a.clicks += safe(r.clicks);
      a.conversions += safe(r.conversions);
      a.conversions_value += safe(r.conversions_value);
      a.total_checkout += safe(r.total_checkout);
      a.total_web_sessions += safe(r.total_web_sessions);
      a.outbound_clicks += safe(r.outbound_clicks);
      a.engagement += safe(r.engagement);
      a.video_3sec_views += safe(r.video_3sec_views);
      a.video_mrc_views += safe(r.video_mrc_views);
      if (safe(r.video_avg_watchtime) > 0) { a.video_watchtime_sum += safe(r.video_avg_watchtime); a.video_rows += 1; }
      a.save_rate_sum += safe(r.save_rate); a.engagement_rate_sum += safe(r.engagement_rate); a.ctr_rows += 1;
    }
    return Object.values(agg).map(a => ({
      ...a,
      ad_count: a.ad_ids.size,
      campaign_name: [...a.campaign_names][0] || '—',
      ad_group_name: [...a.ad_group_names][0] || '—',
      ad_name: [...a.ad_names][0] || '—',
      ctr: a.impressions > 0 ? a.clicks / a.impressions : 0,
      cpc: a.clicks > 0 ? a.spend / a.clicks : 0,
      cpm: a.impressions > 0 ? (a.spend / a.impressions) * 1000 : 0,
      cpa: a.conversions > 0 ? a.spend / a.conversions : 0,
      roas: a.spend > 0 ? a.conversions_value / a.spend : 0,
      outbound_ctr: a.impressions > 0 ? a.outbound_clicks / a.impressions : 0,
      avg_save_rate: a.ctr_rows > 0 ? a.save_rate_sum / a.ctr_rows : 0,
      avg_engagement_rate: a.ctr_rows > 0 ? a.engagement_rate_sum / a.ctr_rows : 0,
      avg_watchtime: a.video_rows > 0 ? a.video_watchtime_sum / a.video_rows : 0,
    }));
  }, [rows]);

  const creativeTypes = useMemo(() => {
    const s = new Set();
    pinAgg.forEach(p => { if (p.creative_type) s.add(p.creative_type); });
    return ['all', ...Array.from(s)];
  }, [pinAgg]);

  const filtered = useMemo(() => {
    const list = creativeTypeFilter === 'all'
      ? pinAgg
      : pinAgg.filter(p => p.creative_type === creativeTypeFilter);
    const k = sort.key;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => ((a[k] ?? 0) - (b[k] ?? 0)) * dir);
  }, [pinAgg, creativeTypeFilter, sort]);

  const selectedPin = useMemo(
    () => filtered.find(p => p.pin_id === selectedPinId) || filtered[0] || null,
    [filtered, selectedPinId]
  );

  useEffect(() => {
    if (!selectedPinId && filtered.length > 0) {
      setSelectedPinId(filtered[0].pin_id);
    }
  }, [filtered, selectedPinId]);

  const totals = useMemo(() => {
    const t = filtered.reduce((acc, p) => {
      acc.spend += p.spend;
      acc.impressions += p.impressions;
      acc.clicks += p.clicks;
      acc.conversions += p.conversions;
      acc.conversions_value += p.conversions_value;
      return acc;
    }, { spend: 0, impressions: 0, clicks: 0, conversions: 0, conversions_value: 0 });
    return {
      ...t,
      roas: t.spend > 0 ? t.conversions_value / t.spend : 0,
      ctr: t.impressions > 0 ? t.clicks / t.impressions : 0,
    };
  }, [filtered]);

  const SortableTh = ({ k, label }) => (
    <th
      className="py-2 px-3 text-right cursor-pointer select-none"
      onClick={() => setSort(prev => ({ key: k, dir: prev.key === k && prev.dir === 'desc' ? 'asc' : 'desc' }))}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && <span>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );

  return (
    <div className="min-h-screen p-4 space-y-6 max-w-screen-2xl mx-auto w-full overflow-x-hidden bg-[#f6f8ff]/70">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-5 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-red-50 border border-red-100 shadow-sm rounded-xl">
            <img
              src="/pinterestlogopng.png"
              alt="Pinterest Logo"
              className="w-8 h-8 object-contain"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Creative Performance</h1>
            <p className="text-sm text-gray-500 mt-1">Analyze your Pinterest pin creatives</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {creativeTypes.length > 1 && (
            <select
              value={creativeTypeFilter}
              onChange={(e) => setCreativeTypeFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-red-100 outline-none transition-all hover:border-gray-300 min-w-[140px]"
            >
              {creativeTypes.map(t => <option key={t} value={t}>{t === 'all' ? 'All types' : t}</option>)}
            </select>
          )}
          <select
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-red-100 outline-none transition-all hover:border-gray-300"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            {DATE_RANGE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <div className="relative">
            <button
              onClick={() => setShowSyncMenu(s => !s)}
              className="px-2.5 py-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50 transition-all"
              title="Sync options"
            >
              <Settings className="w-4 h-4" />
            </button>
            {showSyncMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border rounded-xl shadow-xl z-30 overflow-hidden">
                <button
                  className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 disabled:opacity-50"
                  disabled={syncing}
                  onClick={() => triggerSync('ad-performance')}
                >
                  Sync ad performance
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-t disabled:opacity-50"
                  disabled={syncing}
                  onClick={() => triggerSync('pin-details')}
                >
                  Sync pin details
                </button>
                {syncStatus && (
                  <div className="px-3 py-2 text-[10px] text-gray-500 border-t bg-gray-50 break-all font-mono max-h-32 overflow-auto">{syncStatus}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Performance Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Impressions', value: fmtLarge(totals.impressions), icon: Eye, color: 'blue' },
          { label: 'Revenue', value: fmtTRY(totals.conversions_value), icon: BarChart, color: 'emerald' },
          { label: 'Spend', value: fmtTRY(totals.spend), icon: BarChart3, color: 'indigo' },
          { label: 'Conversions', value: fmtLarge(totals.conversions), icon: ShoppingCart, color: 'purple' },
          { label: 'ROAS', value: totals.roas.toFixed(2) + 'x', icon: TrendingUp, color: 'rose' },
        ].map((metric, idx) => (
          <div key={idx} className="p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow bg-white">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl bg-${metric.color}-50`}>
                  <metric.icon className={`w-5 h-5 text-${metric.color}-600`} />
                </div>
                <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
                  {DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label || ''}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">{metric.label}</p>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>
      )}

      {/* Main Layout: Creative List (left) + Preview (right) */}
      <div className="flex flex-col lg:flex-row gap-6 relative">
        {/* Left: Creative list */}
        <div className="w-full lg:w-7/12 space-y-6">
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
            <div className="p-4 border-b bg-white text-gray-800 font-medium flex justify-between items-center">
              <span className="text-sm font-medium">Pins</span>
              <span className="text-xs text-gray-500">Total: {filtered.length}</span>
            </div>
            <div className="overflow-x-auto">
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-left text-gray-500 font-semibold">
                      <th className="py-2 px-3">Pin</th>
                      <SortableTh k="spend" label="Spend" />
                      <SortableTh k="conversions_value" label="Revenue" />
                      <SortableTh k="conversions" label="Conv." />
                      <SortableTh k="clicks" label="Clicks" />
                      <SortableTh k="impressions" label="Impr." />
                      <SortableTh k="cpm" label="CPM" />
                      <SortableTh k="roas" label="ROAS" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr><td colSpan={8} className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={8} className="py-10 text-center text-sm text-gray-500">No data. Try a wider date range or run sync (gear icon → Sync).</td></tr>
                    ) : (
                      filtered.map((p, idx) => {
                        const isSelected = selectedPin?.pin_id === p.pin_id;
                        return (
                          <tr
                            key={p.pin_id || idx}
                            className={`cursor-pointer transition-colors ${isSelected ? 'bg-red-50 border-l-2 border-red-500' : 'hover:bg-gray-50'}`}
                            onClick={() => setSelectedPinId(p.pin_id)}
                          >
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="relative w-9 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                  {p.image_url_400 || p.image_url_600 ? (
                                    <img src={p.image_url_400 || p.image_url_600} alt="" className="w-full h-full object-cover" loading="lazy" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                                      <ImageIcon className="w-3.5 h-3.5" />
                                    </div>
                                  )}
                                  {p.is_video && (
                                    <div className="absolute bottom-0.5 right-0.5 bg-black/60 rounded-full p-0.5">
                                      <Play className="w-2 h-2 text-white" fill="white" />
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="text-xs font-medium text-gray-800 truncate max-w-[220px]">{p.pin_title || p.ad_name}</div>
                                  <div className="text-[10px] text-gray-500 truncate max-w-[220px]">{p.creative_type || '—'} · {p.ad_count} ad{p.ad_count > 1 ? 's' : ''}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtTRY(p.spend)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtTRY(p.conversions_value)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtNum(p.conversions)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtNum(p.clicks)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtNum(p.impressions)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{p.cpm > 0 ? fmtTRY(p.cpm) : '—'}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{p.roas > 0 ? p.roas.toFixed(2) + 'x' : '—'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Preview */}
        <div className="w-full lg:w-5/12 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden sticky top-4">
            {selectedPin ? (
              <>
                {/* Preview body */}
                <div className="p-4">
                  <PinPreview pin={selectedPin} />
                </div>
                {/* Detail metrics */}
                <div className="border-t border-gray-100 p-4 space-y-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">{selectedPin.creative_type || 'Pin'}</div>
                    <div className="text-sm font-semibold text-gray-900 mt-0.5">{selectedPin.pin_title || selectedPin.ad_name || 'Untitled'}</div>
                    {selectedPin.pin_description && <p className="text-xs text-gray-600 mt-1 line-clamp-3">{selectedPin.pin_description}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-[9px] uppercase text-gray-500 font-medium">Campaign</div>
                      <div className="text-gray-800 truncate mt-0.5">{selectedPin.campaign_name}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-[9px] uppercase text-gray-500 font-medium">Ad Group</div>
                      <div className="text-gray-800 truncate mt-0.5">{selectedPin.ad_group_name}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Spend', value: fmtTRY(selectedPin.spend) },
                      { label: 'Revenue', value: fmtTRY(selectedPin.conversions_value) },
                      { label: 'Impressions', value: fmtNum(selectedPin.impressions) },
                      { label: 'Clicks', value: fmtNum(selectedPin.clicks) },
                      { label: 'CTR', value: fmtPct(selectedPin.ctr) },
                      { label: 'Outbound CTR', value: fmtPct(selectedPin.outbound_ctr) },
                      { label: 'Save rate', value: fmtPct(selectedPin.avg_save_rate), highlight: 'pink' },
                      { label: 'Engagement rate', value: fmtPct(selectedPin.avg_engagement_rate) },
                      { label: 'Conversions', value: fmtNum(selectedPin.conversions) },
                      { label: 'ROAS', value: selectedPin.roas > 0 ? selectedPin.roas.toFixed(2) + 'x' : '—',
                        highlight: selectedPin.roas >= 2 ? 'emerald' : selectedPin.roas >= 1 ? 'amber' : null },
                      { label: 'CPC', value: selectedPin.cpc > 0 ? fmtTRY(selectedPin.cpc) : '—' },
                      { label: 'CPA', value: selectedPin.cpa > 0 ? fmtTRY(selectedPin.cpa) : '—' },
                    ].map((m, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-2">
                        <div className="text-[9px] uppercase text-gray-500 font-medium">{m.label}</div>
                        <div className={`text-xs font-semibold mt-0.5 ${
                          m.highlight === 'pink' ? 'text-pink-600' :
                          m.highlight === 'emerald' ? 'text-emerald-600' :
                          m.highlight === 'amber' ? 'text-amber-600' : 'text-gray-900'
                        }`}>{m.value}</div>
                      </div>
                    ))}
                  </div>
                  {selectedPin.is_video && (
                    <div className="bg-purple-50 border border-purple-100 rounded-lg p-2">
                      <div className="text-[10px] uppercase text-purple-700 font-medium">Video metrics</div>
                      <div className="grid grid-cols-3 gap-2 mt-1.5 text-[11px]">
                        <div><span className="text-gray-500">3s views:</span> <strong>{fmtNum(selectedPin.video_3sec_views)}</strong></div>
                        <div><span className="text-gray-500">MRC views:</span> <strong>{fmtNum(selectedPin.video_mrc_views)}</strong></div>
                        <div><span className="text-gray-500">Avg watch:</span> <strong>{selectedPin.avg_watchtime > 0 ? selectedPin.avg_watchtime.toFixed(1) + 's' : '—'}</strong></div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <div className="text-[11px] text-gray-500">
                      <Layers className="w-3 h-3 inline mr-1" /> {selectedPin.ad_count} ad{selectedPin.ad_count > 1 ? 's' : ''}
                    </div>
                    {selectedPin.pin_url && (
                      <a
                        href={selectedPin.pin_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:text-red-700 hover:underline"
                      >
                        View on Pinterest <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="p-10 text-center text-sm text-gray-400">Select a pin to see preview & metrics</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Pin preview card (Pinterest-style: rounded image with overlay) ────────
function PinPreview({ pin }) {
  const [videoFailed, setVideoFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const imgSrc = pin.image_url_1200 || pin.image_url_600 || pin.image_url_400 || pin.image_url_original || null;

  return (
    <div className="relative mx-auto" style={{ maxWidth: 320 }}>
      <div className="relative rounded-3xl overflow-hidden shadow-md border border-gray-200 bg-gray-50">
        {pin.is_video && pin.video_url && !videoFailed ? (
          <video
            src={pin.video_url}
            poster={pin.video_thumbnail_url || imgSrc || undefined}
            controls
            playsInline
            className="w-full h-auto block"
            style={{ aspectRatio: '2 / 3', objectFit: 'cover', background: '#000' }}
            onError={() => setVideoFailed(true)}
          />
        ) : imgSrc && !imageFailed ? (
          <img
            src={imgSrc}
            alt={pin.pin_title || ''}
            className="w-full h-auto block"
            style={{ aspectRatio: '2 / 3', objectFit: 'cover' }}
            onError={() => setImageFailed(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full flex flex-col items-center justify-center text-gray-400 text-xs" style={{ aspectRatio: '2 / 3' }}>
            <ImageIcon className="w-8 h-8 mb-2" />
            No preview available
          </div>
        )}
        {pin.is_video && (
          <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full flex items-center gap-1">
            <Play className="w-2.5 h-2.5" fill="white" /> Video
          </div>
        )}
        <a
          href={pin.pin_url || '#'}
          target="_blank"
          rel="noreferrer"
          className="absolute bottom-2 right-2 bg-[#e60023] hover:bg-[#bd081c] text-white text-[11px] font-semibold px-3 py-1.5 rounded-full shadow-md inline-flex items-center gap-1"
        >
          <Bookmark className="w-3 h-3" /> Save
        </a>
      </div>
    </div>
  );
}
